<script setup>
import {useDebounceFn} from "@vueuse/core"
import {v4 as uuidv4} from "uuid"
import {computed} from "vue"
import {useI18n} from "vue-i18n"

const props = defineProps({
  modelValue: {
    type: [Number, String],
    default: ''
  },
  label: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  validator: {
    type: Object,
    default: () => {
    }
  },
  error: {
    type: String,
    default: ''
  },
  type: {
    type: String,
    default: 'text'
  },
  phonePrefix: {
    type: String,
    default: ''
  },
  autocomplete: {
    type: String,
    default: ''
  }
})

const { t } = useI18n()

const errorMessage = computed(() => {
  if (props.validator?.$errors.length) {
    return props.validator.$errors.map(
        error => t(error.$message)
    ).join(', ')
  }

  return props.error ? t(props.error) : ''
})

const emits = defineEmits(['update:modelValue'])

const id = computed(() => `input-${uuidv4()}`)

const handleInputValueUpdate = useDebounceFn((event) => {
  emits("update:modelValue", event.target.value)
}, 250, { maxWait: 5000 })

</script>

<template>
  <div class="input-group">
    <label v-if="label" :for="id" class="input-label">
      {{ label }}
    </label>

    <div class="input-group__container">
      <input
        :class="{
          'input-group__input': true,
          'error': !!errorMessage,
          'input-phone_prefix_offset': usePhonePrefix
        }"
        :id="id"
        :value="modelValue"
        v-bind="$attrs"
        :placeholder="placeholder"
        :type="type"
        :autocomplete="autocomplete"
        @input="handleInputValueUpdate"
        @keydown.tab="$emit('update:modelValue', $event.target.value)"
        @keydown.enter="$emit('update:modelValue', $event.target.value)"
        @change="$emit('update:modelValue', $event.target.value)"
      />
    </div>
    <label class="input-error" v-if="errorMessage">
      {{ errorMessage }}
    </label>
  </div>
</template>

<style lang="scss">
  .input-group__container {
    position: relative;
    display: flex;
    align-items: center;

    .phone_prefix {
      position: absolute;
      padding-left: 24px;
      font-size: 1.6rem;
      line-height: 1.92rem;
      font-weight: 500;
      font-family: var(--font-archivo);
      color: var(--color-text-secondary);
    }

    .input-phone_prefix_offset {
      padding-left: 6.5rem;
    }
  }
</style>